<template>
  <LayoutOverlay class="modal-preview-files" @closeOverlay="closeModal()">
    <OverlayCard
      v-if="showConfirmDelete"
      :backgroundTransparent="true"
      @closeOverlay="showConfirmDelete = false"
    >
      <ConfirmDeleteAttachmentCard
        :buttonText="t('general__text__delete') + ' ' + t('general__data__file(s)').toLowerCase()"
        @closeOverlay="showConfirmDelete = false"
        @confirmDelete="deleteFile()"
      />
    </OverlayCard>
    <div class="container">
      <div class="header">
        <p class="header__title">{{ piniaStore.selectedFile?.name ?? '' }}</p>
        <div class="header__buttons">
          <TooltipStandardV2
            v-if="store.getters.hasFileAdminAccess"
            :text="t('general__text__delete')"
            :arrowPosition="'top-center'"
            :verticalOffset="2"
          >
            <ButtonText
              class="header__buttons__button"
              :iconLeft="iconTrash2Small"
              size="extra-small"
              @onClicked="showConfirmDelete = true"
            ></ButtonText>
          </TooltipStandardV2>
          <TooltipStandardV2
            v-if="store.getters.hasFileEditorAccess"
            :text="t('general__text__archive')"
            :arrowPosition="'top-center'"
            :verticalOffset="2"
          >
            <ButtonText
              class="header__buttons__button"
              :iconLeft="iconArchiveSmall"
              size="extra-small"
              @onClicked="archiveFile()"
            ></ButtonText>
          </TooltipStandardV2>
          <TooltipStandardV2
            :text="t('general__text__download')"
            :arrowPosition="'top-center'"
            :verticalOffset="2"
          >
            <ButtonText
              class="header__buttons__button"
              :iconLeft="iconDownloadSmall"
              size="extra-small"
              @onClicked="downloadFile()"
            ></ButtonText>
          </TooltipStandardV2>

          <ButtonText
            class="header__buttons__button"
            :iconLeft="iconXSmall"
            size="extra-small"
            @onClicked="closeModal()"
          ></ButtonText>
        </div>
      </div>
      <div class="content" :class="{ 'content__overflow-hidden': fileType === 'pdf' }">
        <VuePdfEmbed
          v-if="fileBlobSource && !loadingFile && fileType === 'pdf'"
          class="content__pdf"
          :source="fileBlobSource"
          :width="700"
          :page="pdfCurrentPage"
          @loaded="onPdfLoaded"
        />
        <div
          v-if="fileBlobSource && !loadingFile && fileType === 'pdf'"
          class="content__navigation"
        >
          <button class="content__navigation__button" @click="changePage('prev')">
            <IconChevronLeft />
          </button>
          <span class="content__navigation__text">{{ pdfCurrentPage }} / {{ pdfNumPages }}</span>
          <button class="content__navigation__button" @click="changePage('next')">
            <IconChevronRight />
          </button>
        </div>
        <img
          v-if="fileBlobSource && !loadingFile && fileType !== 'pdf'"
          class="content__image"
          :src="fileBlobSource"
          :alt="t('general__text__preview-not-available-for-file-type')"
        />
        <p v-if="showPreviewFailedText && !loadingFile" class="content__text">
          {{ t('general__text__preview-failed') }}
        </p>
        <div v-if="loadingFile" class="content__loading-spinner">
          <PulseLoader color="#3f46d6" size="16px" />
        </div>
      </div>
    </div>
  </LayoutOverlay>
</template>
<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import VuePdfEmbed from 'vue-pdf-embed'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import IconChevronRight from '@/design-system/assets/icons/IconChevronRight.vue'
import IconChevronLeft from '@/design-system/assets/icons/IconChevronLeft.vue'
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import OverlayCard from '@/design-system/molecules/OverlayCard.vue'
import ConfirmDeleteAttachmentCard from '@/components/features/attachments/ConfirmDeleteAttachmentCard.vue'
import TooltipStandardV2 from '@/design-system/atoms/TooltipStandardV2.vue'
import { archiveAttachments, deleteAttachments } from '@/services/attachments.service'
import { getAttachmentFileBlob } from '@/services/files.service'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import iconTrash2Small from '@/design-system/assets/icons/Icon-Trash-2-small.svg'
import iconArchiveSmall from '@/design-system/assets/icons/Icon-Archive-Small.svg'
import iconDownloadSmall from '@/design-system/assets/icons/Icon-Download-small.svg'
import iconXSmall from '@/design-system/assets/icons/Icon-X-Small.svg'
import type { PDFDocumentProxy } from 'pdfjs-dist/types/web/pdf_viewer'

// import useFileList from '@/hooks/files/useFileList'
// emits
interface EMITS {
  (e: 'closePreviewFileModal', val: boolean)
}
const emits = defineEmits<EMITS>()

// variables
const { t } = useI18n()
const store = useStore()
const piniaStore = useGeneralStore()
// const { removeFile } = useFileList()

const fileBlobSource = ref('')
const fileType = ref<string | undefined>('')
const loadingFile = ref(false)
const pdfCurrentPage = ref(1)
const pdfNumPages = ref(1)
const showConfirmDelete = ref(false)
const showPreviewFailedText = ref(false)

// computed

// methods
async function archiveFile() {
  if (!piniaStore.selectedFile) return
  try {
    const id = piniaStore.selectedFile.attachmentId
    if (!id) return
    const response = await archiveAttachments([id])
    // For future bulk operations, check "succeeded" array for each file insead of current "success" boolean
    if (response?.data.success) {
      // remove file from filesList in store
      const filesArray = piniaStore.filesList.filter((file) => file.attachmentId !== id)
      piniaStore.setFilesList(filesArray)
      piniaStore.setSelectedFile(null)
      closeModal()
    }
    if (!response?.data.success) {
      store.commit('setInfoMessage', {
        type: 'error',
        message: `${t('general__data__file-archive-failed')}, ${t(
          'general__text__retry-later-again'
        ).toLowerCase()}`
      })
    }
  } catch (e) {
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${t('general__data__file-archive-failed')}, ${t(
        'general__text__retry-later-again'
      ).toLowerCase()}`
    })
    console.error(e)
  }
}
function changePage(change: string) {
  if (change === 'next') {
    if (pdfCurrentPage.value < pdfNumPages.value) pdfCurrentPage.value += 1
    return
  }
  if (pdfCurrentPage.value !== 1) pdfCurrentPage.value -= 1
}
function closeModal() {
  emits('closePreviewFileModal', false)
}
async function deleteFile() {
  if (!piniaStore.selectedFile) return
  try {
    const entityId = piniaStore.selectedFile.entityId
    const id = piniaStore.selectedFile.attachmentId
    if (!entityId || !id) return
    const response = await deleteAttachments([piniaStore.selectedFile.attachmentId])
    // For future bulk operations, check "succeeded" array for each file insead of current "success" boolean
    if (response?.data.success) {
      // remove file from filesList in store
      const filesArray = piniaStore.filesList.filter((file) => file.attachmentId !== id)
      piniaStore.setFilesList(filesArray)
      piniaStore.setSelectedFile(null)
      closeModal()
    }
    if (!response?.data.success) {
      store.commit('setInfoMessage', {
        type: 'error',
        message: `${t('general__data__file-delete-failed')}, ${t(
          'general__text__retry-later-again'
        ).toLowerCase()}`
      })
    }
  } catch (e) {
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${t('general__data__file-delete-failed')}, ${t(
        'general__text__retry-later-again'
      ).toLowerCase()}`
    })
    console.error(e)
  }
}
async function downloadFile() {
  if (!piniaStore.selectedFile?.url) return
  try {
    const file = piniaStore.selectedFile
    if (!file.realmId || !file.storageName) return
    const res = await getAttachmentFileBlob({
      realmId: file.realmId,
      storageName: file.storageName
    })
    if (!res) return setDownloadFailedMessage()
    const fileUrl = window.URL.createObjectURL(new Blob([res.data]))
    const fileLink = document.createElement('a')
    fileLink.href = fileUrl
    fileLink.setAttribute('download', piniaStore.selectedFile.name)
    document.body.appendChild(fileLink)
    fileLink.click()
  } catch (e) {
    setDownloadFailedMessage()
    console.error(e)
  }
}
async function getPreviewFile() {
  if (!piniaStore.selectedFile?.url) return
  try {
    fileType.value = piniaStore.selectedFile.fileType ?? ''
    loadingFile.value = true
    const file = piniaStore.selectedFile
    if (!file.realmId || !file.storageName) return
    const res = await getAttachmentFileBlob({
      realmId: file.realmId,
      storageName: file.storageName
    })
    if (!res || res.status !== 200) {
      showPreviewFailedText.value = true
      return
    }
    const blob =
      fileType?.value === 'svg' ? new Blob([res.data], { type: 'image/svg+xml' }) : res.data
    fileBlobSource.value = window.URL.createObjectURL(blob)
    showPreviewFailedText.value = false
  } catch (e) {
    showPreviewFailedText.value = true
    console.error(e)
  } finally {
    loadingFile.value = false
  }
}
function setDownloadFailedMessage() {
  store.commit('setInfoMessage', {
    type: 'error',
    message: `${t('general__data__file-download-failed')}, ${t(
      'general__text__retry-later-again'
    ).toLowerCase()}`
  })
}

function onPdfLoaded(pdf: PDFDocumentProxy) {
  pdfNumPages.value = pdf.numPages
  if (pdfCurrentPage.value > pdf.numPages) pdfCurrentPage.value = pdf.numPages
}

onMounted(() => {
  getPreviewFile()
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';
.container {
  background-color: variables.$color-pure-white;
  border-radius: variables.$border-radius-small;
  display: flex;
  flex-direction: column;
  left: 50%;
  max-height: 85vh;
  max-width: 60vw;
  min-height: 100px;
  min-width: 300px;
  padding: 24px;
  position: absolute;
  top: 49%;
  transform: translate(-50%, -50%);
  width: fit-content;
  .content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow-y: auto;
    text-align: center;
    &__image {
      height: intrinsic; // Possibly solves stretched image issue in Safari
      margin: auto;
      max-width: 100%;
    }
    &__loading-spinner {
      margin: 56px auto;
    }
    &__navigation {
      align-items: center;
      color: variables.$color-greyscale-content;
      display: flex;
      gap: 10px;
      justify-content: center;
      padding-top: 8px;
      @include font-mixins.desktop-regular-paragraph;
      &__button {
        all: unset;
        font-size: 24px;
        height: 24px;
        text-align: center;
        width: 24px;
        cursor: pointer;
        &:focus-visible {
          outline: auto;
        }
      }
      &__text {
        width: 80px;
      }
    }
    &__overflow-hidden {
      overflow-y: hidden;
    }
    &__pdf {
      overflow-x: hidden;
      overflow-y: auto;
      padding-bottom: 4px; // Necessary for correct scroll bar activation
      @include mixins.scrollbar-vertical;
    }
    &__text {
      color: variables.$color-error;
      margin: 32px 0 48px 0;
      min-width: 300px;
      @include font-mixins.desktop-regular-paragraph;
      @media screen and (max-width: 1024px) {
        min-width: 250px;
      }
    }
    @include mixins.scrollbar-vertical;
  }
  .header {
    display: flex;
    margin-bottom: 16px;
    &__buttons {
      display: flex;
      flex-direction: row;
      gap: 4px;
      margin-left: auto;
      &__button {
        height: 24px;
        min-width: 24px;
        padding: 0;
        width: 24px;
        &:nth-child(3) {
          margin-left: 8px;
        }
      }
    }
    &__title {
      color: variables.$color-greyscale-content;
      margin: 0;
      margin-right: 16px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: max-content;
      @include font-mixins.desktop-bold-paragraph;
    }
  }
}
</style>
